/**
 * The app destinations a web page can deep-link into, and the one place
 * a page is turned into a URL the installed apps actually open.
 *
 * TASK-590. The grammar is the cross-platform deep-link contract
 * (`wiki/contracts/metro-deeplink-catalog.md`, mirrored by
 * `src/deeplink/registry.ts`, the iOS `DeepLinkRegistry` and the Android
 * `MainActivity.handleIntent`): custom scheme `london-tube://<host>`,
 * where the release-navigable hosts are `stations`, `route` and `map`.
 * Nothing here invents a destination — a page whose surface the apps do
 * not have (line pages, legal pages) maps to the map home, which is the
 * app's landing screen, rather than to a link that resolves to nothing.
 *
 * Two consumers:
 * - the prerender pipeline, for the iOS Smart App Banner `app-argument`
 *   (Universal-Link form — iOS rewrites it through
 *   `UniversalLinkNormalizer` into this same grammar);
 * - `AndroidInstallBanner`, for the `intent://` link that opens the
 *   installed Android app or falls through to the site's `/go/` tracking
 *   link (TASK-1170).
 */
import { StoreLinks } from './storeLinks.mjs'
import { BASE_URL } from './locales.mjs'

/** Custom scheme both mobile apps register for deep links. */
const SCHEME = 'london-tube'

/**
 * Builds the `?from=&to=` query of a route deep link. Both slugs are
 * optional in the contract (`>= 1` to resolve); an empty string counts
 * as absent so a half-known pair still produces a valid link.
 */
function routeQuery(fromSlug, toSlug) {
  const params = []
  if (typeof fromSlug === 'string' && fromSlug !== '') {
    params.push(`from=${encodeURIComponent(fromSlug)}`)
  }
  if (typeof toSlug === 'string' && toSlug !== '') {
    params.push(`to=${encodeURIComponent(toSlug)}`)
  }
  return params.length === 0 ? '' : `?${params.join('&')}`
}

export const AppDeepLinks = Object.freeze({
  /** Station detail — `london-tube://stations/<slug>`. */
  station(slug) {
    return `${SCHEME}://stations/${encodeURIComponent(slug)}`
  },

  /**
   * Route planner, pre-filled — `london-tube://route?from=&to=`.
   * Returns the bare planner when neither slug is known: the host still
   * resolves, matching the catalog rule that both params are optional.
   */
  route(fromSlug, toSlug) {
    return `${SCHEME}://route${routeQuery(fromSlug, toSlug)}`
  },

  /**
   * Map home — the app's landing screen, and therefore the fallback for
   * every page without an app-side surface of its own.
   */
  map() {
    return `${SCHEME}://map`
  },

  /**
   * Smart App Banner `app-argument` for a curated journey page
   * (`/routes/<from>-to-<to>`).
   *
   * The page's own canonical is NOT usable here: `routes` is not a
   * deep-link host, so iOS's `UniversalLinkNormalizer` resolves it to
   * nothing and the app opens on its home screen, dropping the journey
   * the visitor was reading. `/route?from=&to=` IS both AASA-claimed and
   * normalizable, so it survives the round trip into the app's route
   * planner.
   */
  routeAppArgument(fromSlug, toSlug) {
    return `${BASE_URL}/route${routeQuery(fromSlug, toSlug)}`
  },

  /**
   * Android `intent://` link: opens the installed app at `deepLink`, or
   * sends the browser to `fallbackUrl` (Chrome follows
   * `S.browser_fallback_url` when no app resolves the intent). The site's
   * banner passes its `/go/site-smart-banner` tracking link (TASK-1170), so
   * a visitor without the app reaches Play through the links service and
   * the click lands in the link report.
   *
   * The fallback URL is percent-encoded as an intent extra —
   * `encodeURIComponent` escapes `;`, `=` and `&`, so a query in it cannot
   * terminate the `#Intent` fragment early.
   *
   * @param {string} deepLink a `london-tube://…` URL from this registry
   * @param {string} fallbackUrl an `https://` URL — Chrome ignores any other scheme
   */
  androidIntent(deepLink, fallbackUrl) {
    const prefix = `${SCHEME}://`
    if (!deepLink.startsWith(prefix)) {
      throw new Error(`androidIntent expects a ${prefix} deep link, got: ${deepLink}`)
    }
    if (!fallbackUrl.startsWith('https://')) {
      throw new Error(`androidIntent expects an https:// fallback, got: ${fallbackUrl}`)
    }
    return (
      `intent://${deepLink.slice(prefix.length)}` +
      `#Intent;scheme=${SCHEME};package=${StoreLinks.ANDROID_PACKAGE};` +
      `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`
    )
  },
})
